/**
 * Mount/dock for the production ChatPanel. Reproduces the legacy
 * ScopedAIPanel: reads uiSlice, derives the scoped AgentContext from the
 * focused curriculum route, and renders the panel as a fixed 420px column on
 * desktop or a right-anchored Drawer on mobile.
 */

import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ChatPanel from './ChatPanel';

import type { AgentContext, AgentFocus } from '@/shared/types/chat';
import { closeScopedAiPanel } from '@/shared/store/uiSlice';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { refreshCourseLinkContent } from '@/features/resources/services/googleWorkspaceService';

interface ChatPanelDockProps {
  enabled: boolean;
}

const PANEL_WIDTH = 420;

const ChatPanelDock: React.FC<ChatPanelDockProps> = ({ enabled }) => {
  const dispatch = useAppDispatch();
  const { isMobile, scopedAiPanelOpen, scopedAiContext } = useAppSelector((state) => state.ui);
  const courseId = scopedAiContext.courseId;

  // Fire-and-forget when the chat opens: silently refresh extracted text for the
  // course's Google file links (Docs, Slides) so the next LLM turn reads current
  // content. Re-fires once per open (the ref resets on close) and per course switch.
  const refreshedCourseRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!enabled || !scopedAiPanelOpen) {
      refreshedCourseRef.current = null;
      return;
    }
    if (!courseId || !isFeatureEnabled('linkContentExtraction')) return;
    if (refreshedCourseRef.current === courseId) return;
    refreshedCourseRef.current = courseId;
    void refreshCourseLinkContent(courseId).catch(() => {
      // Best-effort: refresh never blocks chat. Reconnect/auth issues surface via
      // the existing Google flows on the next explicit action.
    });
  }, [enabled, scopedAiPanelOpen, courseId]);

  if (!enabled) return null;
  const focus: AgentFocus | undefined =
    scopedAiContext.level && courseId
      ? {
          level: scopedAiContext.level,
          courseId,
          unitId: scopedAiContext.unitId,
          lessonId: scopedAiContext.lessonId,
          resourceId: scopedAiContext.resourceId,
          resourceKind: scopedAiContext.resourceKind,
        }
      : undefined;

  const context: AgentContext | null = courseId
    ? {
        assistantType: 'course',
        courseId,
        focus,
        profile:
          scopedAiContext.resourceKind === 'document'
            ? 'document'
            : scopedAiContext.resourceKind === 'slide'
              ? 'slides'
              : undefined,
      }
    : null;

  const panelBody = (
    <Box
      sx={{
        width: { xs: '100vw', sm: PANEL_WIDTH },
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {context ? (
        <ChatPanel context={context} onClose={() => dispatch(closeScopedAiPanel())} />
      ) : (
        <>
          <Box
            component="header"
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}>
              AI CoPlanner
            </Typography>
            <IconButton
              size="small"
              aria-label="Close chat"
              onClick={() => dispatch(closeScopedAiPanel())}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Open a curriculum page to start chatting.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open={scopedAiPanelOpen}
        onClose={() => dispatch(closeScopedAiPanel())}
      >
        {panelBody}
      </Drawer>
    );
  }

  if (!scopedAiPanelOpen) return null;

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        borderLeft: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexShrink: 0,
        height: '100%',
        minHeight: 0,
      }}
    >
      {panelBody}
    </Box>
  );
};

export default ChatPanelDock;
